/**
 * Organization Model
 * Matches the Hub Organization data model structure
 * Reference: /american-drone-network-hub/src/data-models/organization.ts
 */

/**
 * Business Type Enum
 * Defines the platform role of the organization (Drone Service Provider vs Farmer).
 * Stored in the `business_type` column.  Separate from `organizationType` which
 * captures the legal entity structure (LLC, S-Corp, Individual, etc.).
 */
export enum BusinessType {
  DroneServiceProvider = 'Drone Service Provider',
  Farmer = 'Farmer',
}

export enum OrganizationType {
  Individual = 'individual',
  Llc = 'llc',
  SCorp = 's-corp',
  CCorp = 'c-corp',
  Partnership = 'partnership',
  SoleProprietorship = 'sole-proprietorship',
  NonProfit = 'non-profit',
  Other = 'other',
}


/**
 * Billing Responsibility Enum
 * Determines who pays for organization's services
 */
export type BillingResponsibility = 'self' | 'parent';

/**
 * Organization interface matching Hub Organization model
 * All fields use camelCase as per frontend conventions
 */
export interface Organization {
  // Base fields (from ModelBase)
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt?: Date | string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  deletedBy?: string | null;

  // Basic Information
  name: string;
  organizationType?: OrganizationType | null;
  businessType?: BusinessType | null;

  // Owner Relationship
  ownerProfileId: string;

  // Tax Identifiers (Phase 1: plaintext, Phase 2: encrypted)
  ein?: string | null;
  ssn?: string | null;

  // Address Fields
  streetAddressLine1: string;
  streetAddressLine2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country: string;

  // Geocoding (future use)
  latitude?: number | null;
  longitude?: number | null;

  // Hierarchical Structure
  parentOrganizationId?: string | null;

  // Billing Configuration
  billingResponsibility: BillingResponsibility;
  stripeCustomerId?: string | null;
  paymentMethodId?: string | null;

  // Optional Contact Info
  email?: string | null;
  phone?: string | null;
}

/**
 * Partial organization for updates
 */
export type OrganizationUpdate = Partial<Organization>;

/**
 * Organization creation payload
 */
export interface OrganizationCreate {
  name: string;
  organizationType?: OrganizationType;
  businessType?: BusinessType;
  ownerProfileId: string;
  streetAddressLine1: string;
  streetAddressLine2?: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  ein?: string;
  ssn?: string;
  billingResponsibility?: BillingResponsibility;
  parentOrganizationId?: string;
  email?: string;
  phone?: string;
}
